import i18next from 'i18next'
import { getPrimeLocale } from '@/prime-config.ts'
import type { PrimeVueConfiguration } from 'primevue/config'
import { captureMissingTranslation } from '@/plugins/sentry'
import { STRAPI_URL } from '@/api/http'

const isKey = (key: string) => {
    const prefixes = ['ui', 'frontend', 'global', 'mail']
    return !/\s/.test(key) && prefixes.some((k) => key.startsWith(k + '.'))
}

export async function initTranslations(primeVueConfig: PrimeVueConfiguration) {
    const { default: defaultTranslations } = await import('@strapi-cms/data/i18n/de_DE.json')

    await i18next.init({
        lng: 'de',
        fallbackLng: 'de',
        ns: ['translation'],
        defaultNS: 'translation',
        resources: {
            de: { translation: defaultTranslations },
        },
        saveMissing: true,
        missingKeyHandler: (
            lngs: readonly string[],
            ns: string,
            key: string,
            _fallbackValue: string,
            _updateMissing: boolean,
            options?: { defaultValue?: unknown }
        ): void => {
            if (!isKey(key) || options?.defaultValue !== undefined) return
            const message = `Missing i18n translation: ${key}`

            if (import.meta.env.DEV) {
                console.error(message)
                return
            }

            captureMissingTranslation(message, ns, key, lngs)
        },
    })

    await loadStrapiTranslations()

    i18next.on('languageChanged', (language: string) => changeLanguage(language, primeVueConfig))

    return i18next
}

function changeLanguage(language: string, primeVueConfig: PrimeVueConfiguration) {
    const locale = primeVueConfig.locale

    if (locale) {
        Object.assign(locale, getPrimeLocale(language))
    }
}

async function loadStrapiTranslations() {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 2500)
    let response: Response | undefined

    try {
        response = await fetch(
            `${STRAPI_URL}/api/translation-entries/i18n?locale=${i18next.language}`,
            { signal: controller.signal }
        )
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`)
        }

        const payload = await response.json()
        i18next.addResourceBundle(i18next.language, 'translation', payload, true, true)
    } catch (error) {
        const { captureTranslationLoadError } = await import('@/plugins/sentry')
        captureTranslationLoadError(error, STRAPI_URL, response?.status)
    } finally {
        clearTimeout(timeoutId)
    }
}
